import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input"
import { useLocation, useSearchParams } from "wouter";
import useMusioApi from "@/contexts/client/musio-client-context";
import { LoadingIndicator } from "@/components/ui/loading-indicator";
import { ReactComponent as Logo } from "@/assets/musio-logos/logo_center_top.svg";
import { SEARCH_PARAM_TOKEN_KEY } from "@/features/recovery";
import { NotificationRow } from "@/components/ui/notification-row";

const ERROR_TOKEN = "There was an error processing your request";
const ERROR_RESET = "There was an error resetting your password. Please check your inputs";
const CONFIRMATION = "Success!";

export default function ResetPassword() {
  const [_, navigate] = useLocation();
  const { musioApi } = useMusioApi();
  const [params] = useSearchParams();
  const token = params.get(SEARCH_PARAM_TOKEN_KEY);

  if (!token) {
    navigate("~/");
    console.log("missing token");
    return;
  }

  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [formError, setFormError] = useState<string>("");

  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  const { isPending: isValidationPending, isError: isValidationError } = musioApi.useQuery("get", "/reset", {
    params: {
      query: {
        token
      }
    }
  }, {
    enabled: !!token,
  });

  const { mutate, isError: isResetError, error: resetError, isPending: isResetPending, isSuccess: isResetSuccess } = musioApi.useMutation("post", "/reset", {
    onSuccess: () => setTimeout(() => {
      console.log("Success");
      navigate("~/account")
    }, 1000),
  });

  const changePassword = useCallback((e: React.FormEvent) => {
    e.preventDefault(); // dont re-render page or else token is lost  
    setFormError("");

    if (password != confirmPassword) {
      setFormError("Passwords do not match");
      return;
    }

    mutate({
      body: {
        token: token,
        password: password
      }
    });
  }, [password, confirmPassword]);

  const hasNotification = formError || isResetSuccess || isResetError;

  // just loading during token validation
  if (isValidationPending) {
    return (
      <div className="w-f>ll h-full flex items-center justify-center">
        <LoadingIndicator className="p-10 musio-glass" />
      </div>
    );
  }

  // the token is invalid, so we dont want the user to have any chance of input
  if (isValidationError) {
    return (
      <div className="flex flex-col items-center justify-center font-inter space-y-2 p-2">
        <div className="w-[128px] h-[128px] flex-shrink-0 flex justify-center items-center">
          <Logo />
        </div>
        <Card className="flex items-center justify-center flex-col musio-glass w-full max-w-[478px] h-full">
          <CardHeader className="flex items-center justify-center card-header-padded">
            <div className="flex items-center justify-between text-musio-lilac">
              <CardTitle className="text-[30px]">Reset Password</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="w-full space-y-2 card-content-padded flex flex-col justify-center items-center">
            <NotificationRow intent={"error"}>
              {ERROR_TOKEN}
            </NotificationRow>
          </CardContent>
          <CardFooter className="card-footer-padded w-full flex flex-col justify-center items-center gap-2">
            <Button className="w-full font-bold bg-musio-green text-musio-mid-gray" onClick={() => navigate("~/")}>
              Return
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center font-inter space-y-2">
      <div className="w-[128px] h-[128px] flex-shrink-0 flex justify-center items-center">
        <Logo />
      </div>
      <form onSubmit={changePassword} className="max-w-[478px] w-full p-2">
        <Card className="flex items-center justify-center flex-col musio-glass h-full">
          <CardHeader className="flex items-center justify-center card-header-padded w-full">
            <div className="flex items-center justify-between text-musio-lilac">
              <CardTitle className="text-[30px]">Recover Password</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 w-full">
            {hasNotification ?
              <NotificationRow intent={(formError || isResetError) ? "error" : "success"}>
                {isResetSuccess ? CONFIRMATION : formError ? formError : resetError?.error?.passwordConstraint ? resetError.error.passwordConstraint : ERROR_RESET}
              </NotificationRow> : null
            }
            <Input
              ref={passwordRef}
              type="password"
              id="password"
              placeholder="New Password."
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="bg-musio-bg-light border-musio-bg-dark w-full"
            />
            <Input
              ref={confirmPasswordRef}
              type="password"
              id="confirm-password"
              placeholder="Confirm Password."
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              className="bg-musio-bg-light border-musio-bg-dark w-full"
            />
          </CardContent>
          <CardFooter className="p-4 w-full flex flex-col justify-center items-center gap-2">
            {isResetPending ? (
              <LoadingIndicator />
            ) : (
              <>
                <Button
                  type="submit"
                  disabled={isResetSuccess}
                  className="w-full font-bold bg-musio-green text-musio-mid-gray"
                >
                  Submit
                </Button>
              </>
            )}
          </CardFooter>
        </Card>
      </form>
    </div>
  );
}
